import MapView from '@arcgis/core/views/MapView';
import { MapViewInputTask } from '../framework/MapViewInputTask';
import { SurfaceDef } from '../model/SurfaceDef';
import { SurfaceBreakline } from '../model/SurfaceBreakline';
import { GroundPoint } from '../model/GroundPoint';
import { esriGeometryEngine, Graphic, Point } from 'cd-eft/utils/esriTypes';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Multipoint from '@arcgis/core/geometry/Multipoint';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import { invalidNextPointSymbol, validNextPointSymbol } from '../resources/esriSymbols';
import { onToolCleanup, onBreaklineSetup } from '../utils/ToolUtils';

export class AddPointBreaklineInputTask extends MapViewInputTask<SurfaceBreakline> {
    private readonly surfaceDef: SurfaceDef;
    protected breaklineToEdit?: SurfaceBreakline;
    private feedbackLayer: GraphicsLayer = new GraphicsLayer({ title: 'moveFeedbackLayer', listMode: 'hide' });
    private allGroundPoints: GroundPoint[] = [];
    private allGroundPointsMP: Multipoint = new Multipoint();
    private readonly tempBreaklinesLayer: GraphicsLayer = new GraphicsLayer({ title: 'tempBreaklinesLayer' });
    private readonly tempHighlightLayer: GraphicsLayer = new GraphicsLayer({ title: 'tempHighlightLayer' });
    private __highlightHandle: any;
    private selectedGroundPoint?: GroundPoint;

    constructor(mapView: MapView, surfaceDef: SurfaceDef) {
        super(mapView);
        this.surfaceDef = surfaceDef;
    }

    protected onSetup(): void {
        super.onSetup();
        this.cursor = 'pointer';
        onBreaklineSetup(this.cursor, this.__highlightHandle, this.tempBreaklinesLayer, this.tempHighlightLayer, this.surfaceDef.breaklines, this.mapView);

        this.allGroundPoints = this.surfaceDef.getAllGroundPoints();
        const multipoint: Multipoint = new Multipoint({ spatialReference: this.mapView.spatialReference });
        for (const point of this.allGroundPoints) {
            multipoint.addPoint(point.generateGeometry(this.mapView.spatialReference));
        }
        this.allGroundPointsMP = multipoint;
        this.mapView.map.add(this.feedbackLayer);
    }

    protected onCleanup(): void {
        super.onCleanup();
        this.mapView.map.remove(this.feedbackLayer);
        this.feedbackLayer.removeAll();
        this.breaklineToEdit = undefined;
        this.selectedGroundPoint = undefined;

        onToolCleanup(this.__highlightHandle, this.tempBreaklinesLayer, this.mapView, this.tempHighlightLayer);
    }

    protected async onImmediateClick(e: __esri.ViewImmediateClickEvent): Promise<void> {
        super.onImmediateClick(e);
        const mapPoint = e.mapPoint;
        if (!this.breaklineToEdit) {
            // First click: select breakline
            const hitResult = await this.mapView.hitTest(e, { include: this.mapView.map.layers });
            const graphic = hitResult.results.find((r) => r.graphic?.attributes?.eft_type === 'SurfaceBreakline')
                ?.graphic;
            if (!graphic) return;
            graphic.symbol = new SimpleLineSymbol({ width: 3, color: 'blue' });
            this.breaklineToEdit = graphic.attributes as SurfaceBreakline;
            this.feedbackLayer.add(graphic.clone());
            return;
        }

        if (this.selectedGroundPoint == undefined) {
            this.selectedGroundPoint = this.findNearestGroundPoint(mapPoint);
            if (!this.selectedGroundPoint) return;
            // Check if duplicate point in breakline
            const selectedPoint = this.selectedGroundPoint.generateGeometry(this.mapView.spatialReference);
            const isDuplicate = (this.breaklineToEdit.groundPoints.findIndex((p) => p === this.selectedGroundPoint) === -1) ? false : true;
            const symbol = isDuplicate ? invalidNextPointSymbol : validNextPointSymbol;
            this.drawHighlightVertex(selectedPoint, symbol);
            if (isDuplicate) this.selectedGroundPoint = undefined;
            return;
        }

        const insertIndex = this.findClosestSegmentIndex(mapPoint);
        if (insertIndex === null) return;
        const updated = this.insertPointAtIndex(this.breaklineToEdit, this.selectedGroundPoint, insertIndex);
        if (await this.surfaceDef.checkBreakLineCrosses(updated, this.mapView)) {
            this.feedbackLayer.removeAll();
            this.selectedGroundPoint = undefined;
            return;
        }
        this.surfaceDef.breaklinesSyncSet.updateItem(this.breaklineToEdit, updated, true);
        this.completeNow(updated);
    }

    private findNearestGroundPoint(mapPoint: Point): GroundPoint {
        const findResult = esriGeometryEngine.nearestVertex(this.allGroundPointsMP, mapPoint);
        let result;
        if (findResult.distance < 11) {
            result = this.allGroundPoints[findResult.vertexIndex];
        }
        return result;
    }

    private drawHighlightVertex(point: __esri.Point, symbol = validNextPointSymbol) {
        this.feedbackLayer.removeAll();
        const graphic = new Graphic({ geometry: point, symbol: symbol });
        graphic.attributes = { type: 'selectedVertex' };
        this.feedbackLayer.add(graphic);
    }

    private findClosestSegmentIndex(mapPoint: Point): number | null {
        if (!this.breaklineToEdit || this.breaklineToEdit.groundPoints.length < 2) return null;

        let minDistance = Infinity;
        let closestSegmentIndex: number | null = null;

        const groundPoints = this.breaklineToEdit.groundPoints;
        const spatialRef = this.mapView.spatialReference;

        for (let i = 0; i < groundPoints.length - 1; i++) {
            const pt1 = groundPoints[i].generateGeometry(spatialRef);
            const pt2 = groundPoints[i + 1].generateGeometry(spatialRef);

            const segment = {
                type: 'polyline',
                paths: [
                    [
                        [pt1.x, pt1.y],
                        [pt2.x, pt2.y],
                    ],
                ],
                spatialReference: spatialRef,
            } as __esri.Polyline;

            const distance = esriGeometryEngine.distance(mapPoint, segment, 'meters');

            if (distance !== null && distance < minDistance) {
                minDistance = distance;
                closestSegmentIndex = i;
            }
        }

        return closestSegmentIndex;
    }

    private insertPointAtIndex(breakline: SurfaceBreakline, point: GroundPoint, index: number): SurfaceBreakline {
        const updated = new SurfaceBreakline(breakline.breaklineId);
        updated.groundPoints.push(...breakline.groundPoints);
        updated.groundPoints.splice(index + 1, 0, point);
        return updated;
    }
}
